import { MeshUtils } from '@/commons/MeshUtils';
import { AnimatedCamera } from '@/commons/camera/AnimatedCamera';
import { CustomOrbitControls } from '@/commons/camera/controls/CustomOrbitControls';
import { DEFAULT_CAMERA_ANIMATION_DURATION } from '@/config';
import { Box3, Object3D, Vector3 } from 'three';
import { Easing, Tween } from '@tweenjs/tween.js';
import type { ICameraControls } from '@/commons/camera/controls/ICameraControls';
import type { TCameraParams } from '@/commons/camera/TCameraParams';

class ArcRotateCamera extends AnimatedCamera {
    isPan: boolean = false;
    _pollOpen = false;
    mouseStartPosition: Vector3 = new Vector3();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _zoomToFitAnimation: Tween<any> | null = null;

    get pollOpen() {
        return this._pollOpen;
    }

    set pollOpen(value: boolean) {
        this._pollOpen = value;
        if (value) {
            this.controls.maxDistance = 2.5;
        } else {
            this.controls.maxDistance = 0.95;
        }
    }

    // #region INITIALIZATION
    protected createControls(rendererDomElement: HTMLCanvasElement): ICameraControls {
        return new CustomOrbitControls(this, rendererDomElement);
    }

    constructor(cameraParams: TCameraParams) {
        super(cameraParams);
        this.initializeControls();
        this.initCameraListener();
    }

    initCameraListener() {
        // this.controls.domElement?.addEventListener('mousedown', this.mouseDownCallback);
        // this.controls.domElement?.addEventListener('mousemove', this.moveCallback);
        // this.controls.domElement?.addEventListener('mouseup', this.mouseUpCallback);
    }
    // #endregion

    // #region CALLBACKS
    mouseDownCallback = (e: MouseEvent) => {
        if (e.button !== 0) {
            this.isPan = false;
            this.controls.enableDamping = true;
            return;
        }
        const { height, width } = this.controls.domElement!.getClientRects()[0];
        const mouseToWorld = MeshUtils.mouseToWorld(
            e.clientX,
            e.clientY,
            { width, height, aspect: width / height },
            this,
            new Vector3(),
            this.radius,
        );
        this.mouseStartPosition.copy(mouseToWorld);
        this.isPan = true;
    };

    zoomOnSelection = (
        objectSelection: Object3D[],
        offset: number = 0,
        duration: number = DEFAULT_CAMERA_ANIMATION_DURATION,
    ): Promise<void> => {
        const box = new Box3();
        for (let i = 0; i < objectSelection.length; i += 1) {
            box.expandByObject(objectSelection[i]);
        }
        const target = new Vector3();
        box.getCenter(target);
        const finalPosition = MeshUtils.fitCameraToSelection(this, target, objectSelection, 0);
        const convPosition = MeshUtils.sphericalFromCartesian(finalPosition.x, target.y, finalPosition.z);
        if (offset) {
            convPosition.radius *= offset;
        }
        const anim = {
            t: 0,
            radius: this.radius,
            posY: this.position.y,
        };
        return new Promise<void>((resolve) => {
            const ending = () => {
                this._zoomToFitAnimation = null;
                resolve();
            };

            this._zoomToFitAnimation = new Tween(anim)
                .to({ t: 1, radius: convPosition.radius, posY: finalPosition.y }, duration)
                .onUpdate(() => {
                    this.position.copy(new Vector3(0, 0, anim.radius).applyQuaternion(this.quaternion)).add(this.controls.target);
                    this.controls.target.copy(this.controls.target.clone().lerp(target, anim.t));
                })
                .onComplete(ending)
                .easing(Easing.Quadratic.InOut)
                .start();
        });
    };

    mouseUpCallback = () => {
        this.isPan = false;
    };

    get panLimit() {
        return this.pollOpen ? -0.1 : 0.7028532044004389 - 0.2 - (1 - this.radius) * 0.1;
    }

    moveCallback = (e: MouseEvent) => {
        if (!this.isPan) return;
        const camHeight = 0.7028532044004389;
        const panOffset = 0.2;
        const { height, width } = this.controls.domElement!.getClientRects()[0];
        const newMouse = MeshUtils.mouseToWorld(
            e.clientX,
            e.clientY,
            { width, height, aspect: width / height },
            this,
            new Vector3(),
            this.radius,
        );
        const sign = Math.sign(newMouse.y - this.mouseStartPosition.y);
        const zoomingFactor = (1 - this.radius) * 0.1;
        const moveVector =
            (((new Vector3(0, this.mouseStartPosition.y, 0).distanceTo(new Vector3(0, newMouse.y, 0)) / height) * 0.025) / this.radius) *
            sign;
        const newValue = Math.min(Math.max(this.position.y + moveVector, this.panLimit), camHeight + panOffset + zoomingFactor);
        this.position.y = newValue;
        this.controls.target.y = newValue;
        this.mouseStartPosition.copy(newMouse);
    };
    // #endregion

    dispose(): void {
        this.controls.domElement?.removeEventListener('mousedown', this.mouseDownCallback);
        this.controls.domElement?.removeEventListener('mousemove', this.moveCallback);
        this.controls.domElement?.removeEventListener('mouseup', this.mouseUpCallback);
        this.controls.dispose();
    }

    render(): void {
        super.render();
        this._zoomToFitAnimation?.update();
    }
}

export { ArcRotateCamera };
